/**
 * JSON contracts for checkpoint review, shared by the API, web and CLI.
 *
 * @module visual-api
 * @see docs/features/test-suite-execution.md
 */
import type { VisualMask, VisualSeverity } from './visual-checkpoint.js';
import type { VisualExplanation, VisualRect, VisualTextChanges } from './visual-review.js';

export type VisualOutcome =
  | 'new'
  | 'unchanged'
  | 'unstable'
  | 'cant_compare'
  | 'healed'
  | 'resolved'
  | 'changed';
export const VISUAL_CHECKPOINT_STATUSES = ['changed', 'healed', 'new', 'unstable', 'all'] as const;
export type VisualCheckpointStatus = (typeof VISUAL_CHECKPOINT_STATUSES)[number];
export type VisualOutcomeCounts = {
  changed: number;
  healed: number;
  new: number;
  unstable: number;
  cantCompare: number;
};

/** Skinny list row. Dates are ISO strings; absent values are always null. */
export type VisualCheckpointListItem = {
  id: string;
  source: 'native' | 'playwright';
  label: string;
  environmentName: string;
  suiteId: string | null;
  suiteName: string | null;
  workflowId: string | null;
  workflowName: string | null;
  specFile: string | null;
  testTitle: string | null;
  executionId: string | null;
  externalTestRunId: string | null;
  outcome: VisualOutcome | null;
  outcomeReason: string | null;
  createdAt: string;
  imageUrl: string | null;
};

export type VisualCheckpointDetail = VisualCheckpointListItem & {
  width: number | null;
  height: number | null;
  severity: VisualSeverity | null;
  explanation: VisualExplanation | null;
  healedMask: VisualMask | null;
  maskMisses: string[];
  regions: VisualRect[];
  textChanges: VisualTextChanges | null;
  changedPercent: number | null;
  regressionMarkedAt: string | null;
  baselineUrl: string | null;
  diffUrl: string | null;
};

export type VisualCheckpointListResponse = {
  ok: true;
  data: { items: VisualCheckpointListItem[]; nextCursor: string | null };
};
export type VisualCheckpointDetailResponse = { ok: true; data: VisualCheckpointDetail };
export type VisualSummaryResponse = { ok: true; data: VisualOutcomeCounts };
